import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { BadgeDollarSign, Check, FileText, MessageSquare, Paperclip, Phone, Zap } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { toast } from 'sonner';
import { isMacPlatform } from '@/lib/platform';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Field, FieldDescription, FieldLabel, FieldLegend, FieldSet } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Kbd, KbdGroup } from '@/components/ui/kbd';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { IconTile } from '@/components/reui/icon-tile';
import { TarjetasRadio, type OpcionTarjeta } from '@/components/comunes/tarjetas-radio';
import { ApiError } from '@/lib/query-client';
import { CampoDecimal } from '@/components/rdt/campo-decimal';
import { faltantesPaso, unirFaltantes } from '@/components/rdt/paso-validacion';
// Solo tipos (se borran al compilar): la página de la obra no carga nada del Avivador.
import type { PasoFormProps, VariantePaso } from '@/components/avivador/panel-avivador-tipos';
import type { ResultadoPaso } from '@/components/avivador/avivador-tipos';
import {
  STEP_KEY_TO_HITO_CODIGO,
  entregaElegida,
  esFechaFutura,
  hitoAplicable,
  hoyIso,
  lifecycle,
  motivoEntregaBloqueada,
  opcionesPeriodo,
  perLabel,
  periodoActual,
  periodoHabilitado,
  periodoPrecargado,
  proximoPaso,
  ultimosPeriodos,
} from '@/components/rdt/rdt-domain';
import type { TipoPaso } from '@/components/rdt/rdt-domain';
import {
  useCompletarHito,
  useCrearChequeo,
  useCrearComentario,
  useCrearMonto,
  useCrearReferencia,
  useCrearSeguimiento,
  useSubirAdjunto,
} from '@/hooks/use-rdt-mutations';
import type { RdtDetalle, TipoAdjunto } from '@/types/api';

/** Solo se usa `obra.cc` (el panel del Avivador no pide `useObra`). */
type ObraRef = PasoFormProps['obra'];

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message || fallback;
  if (err instanceof Error) return err.message;
  return fallback;
}

// «Comentario» se suma al catálogo (Juan, validación v0.12.0/2026-09-15:
// «todo es por actividad» — el comentario es un paso más, ya no un composer
// flotante aparte) — mismo icono/tinte que ya usaba paso-palette.tsx.
const ICONS: Record<TipoPaso, LucideIcon> = {
  chequeo: Zap,
  monto: BadgeDollarSign,
  expediente: FileText,
  hito: Check,
  contacto: Phone,
  adjunto: Paperclip,
  comentario: MessageSquare,
};

// Tono del IconTile del título del form (R1, Ajuste 15/`c-icon-tile-15`): los
// cuatro estados semánticos con `text-{x}-foreground dark:text-{x}` (el
// mismo patrón que el Badge `-light`); los neutros con el token único de la
// app, que ya se adapta solo por tema. `IconTile variant="soft"` deriva el
// fondo/borde de `currentColor`: sin teñir nada más a mano.
const ICON_TONE: Record<TipoPaso, string> = {
  chequeo: 'text-info-foreground dark:text-info',
  monto: 'text-success-foreground dark:text-success',
  expediente: 'text-warning-foreground dark:text-warning',
  hito: 'text-success-foreground dark:text-success',
  contacto: 'text-muted-foreground',
  adjunto: 'text-muted-foreground',
  comentario: 'text-text-tertiary',
};

function PasoFormShell({
  tipo,
  titulo,
  flush,
  variante = 'obra',
  sinTitulo,
  children,
}: {
  tipo: TipoPaso;
  titulo: string;
  /** Dorso de la card v3 del Avivador (design_avivador.md §12.2 punto 3): sin
   *  el borde/halo `border-primary` — ese contorno marca el form contra el
   *  hilo de Actividad que queda DETRÁS en obra-detalle.tsx; en el dorso no
   *  hay hilo detrás, el form ya ocupa el 100% del fondo. Default (ausente)
   *  = mismo render de siempre, sin cambios para el resto de los call-sites
   *  (obra-detalle.tsx/Actividad, el panel de la Lista). */
  flush?: boolean;
  /** 'avivador' (panel del Avivador v2, T4a): sin márgenes ni fondo propios
   *  (el panel maneja el espaciado y pinta el marco). 'obra' (default) = el
   *  render de siempre. */
  variante?: VariantePaso;
  /** Sin la fila de título (el panel pinta `tituloAccion`). */
  sinTitulo?: boolean;
  children: ReactNode;
}) {
  const Icon = ICONS[tipo];
  // R1 (Ajuste tras la crítica #9): en la variante 'avivador' el call-site
  // (panel-avivador-accion.tsx) SIEMPRE pasa `flush` — la rama con el marco
  // propio (borde/halo) nunca corría, el panel ya pinta su propio marco por
  // fuera. Queda un solo layout, sin la condición muerta.
  const className =
    variante === 'avivador'
      ? 'flex flex-col gap-3'
      : `mx-4 mb-2.5 mt-1 flex flex-col gap-2 rounded-[11px] bg-surface-card p-3 ${
          flush ? '' : 'border border-primary/45 shadow-[0_0_0_3px_color-mix(in_srgb,var(--primary)_8%,transparent)]'
        }`;
  return (
    <div className={className} data-paso-form={tipo}>
      {!sinTitulo && (
        <div className="flex items-center gap-1.5 text-xs font-[650]">
          <IconTile variant="soft" size="xs" className={ICON_TONE[tipo]} aria-hidden="true">
            <Icon strokeWidth={2.2} />
          </IconTile>
          {titulo}
        </div>
      )}
      {children}
    </div>
  );
}

// Botones de la fila de acciones en el panel del Avivador: `Button` de ReUI
// tal cual (R1), sin clases propias — el tamaño lo da `size` (`default` en
// el panel, `sm` en la obra) y el anillo de foco único lo dibuja la regla
// global de R0. El primario va con `focusableWhenDisabled` (review T4a, A3):
// deshabilitado sigue en el orden de Tab con `aria-disabled` (no `disabled`
// nativo), así al tabular se lee su `aria-describedby` («Falta: …»). Base UI
// no deja pasar el click ni Enter / Espacio, y los `handleSubmit` tienen su
// guarda.

// Fila final de acciones (Cancelar + el botón primario del form) — misma
// fila, alineada a la derecha, como en el preview (`.paso-form .acts`).
// `disabled` deshabilita "Cancelar" mientras la mutation del form está en
// vuelo (hallazgo 8, review v3): sin esto, un click en Cancelar apenas
// después de disparar el submit corre una carrera contra la respuesta real
// del server y puede resolver el ítem igual, aunque el usuario haya pedido
// cancelar.
// En el panel del Avivador (`enPanel`, T4a §13 7.2) la fila lleva a la
// izquierda el `aviso` («Falta: …», el motivo de la entrega bloqueada o «La
// fecha es futura») y los botones a `size="default"` (en la obra, `size="sm"`).
function PasoFormActs({
  onCancel,
  disabled,
  enPanel,
  aviso,
  children,
}: {
  onCancel: () => void;
  disabled?: boolean;
  enPanel?: boolean;
  aviso?: ReactNode;
  children?: ReactNode;
}) {
  if (enPanel) {
    return (
      <div className="flex flex-wrap items-center justify-end gap-2">
        {aviso}
        <Button type="button" variant="outline" size="default" onClick={onCancel} disabled={disabled}>
          Cancelar
        </Button>
        {children}
      </div>
    );
  }
  return (
    <div className="mt-0.5 flex justify-end gap-[7px]">
      <Button type="button" variant="outline" size="sm" onClick={onCancel} disabled={disabled}>
        Cancelar
      </Button>
      {children}
    </div>
  );
}

/** Lo que va a la izquierda de los botones en el panel, en este orden: el
 *  motivo de la entrega bloqueada (ámbar; reemplaza al «Falta:»), «Falta: …»
 *  (neutro) o, si no falta nada, «La fecha es futura» (ámbar, no bloquea). */
function avisoAcciones(faltan: string[], motivo: string | null, fechaFutura: boolean): { texto: string; ambar: boolean } | null {
  if (motivo) return { texto: motivo, ambar: true };
  if (faltan.length > 0) return { texto: `Falta: ${unirFaltantes(faltan)}`, ambar: false };
  if (fechaFutura) return { texto: 'La fecha es futura', ambar: true };
  return null;
}

// R1: `FieldDescription` en vez de un `<p>` a mano (con `id` para
// `aria-describedby` del botón primario); el ámbar es la única adaptación de
// token permitida acá (`className="text-warning"`) — nunca `FieldError`
// (trae `role=alert` fijo, y esto no bloquea el envío).
function AvisoAcciones({ id, aviso }: { id: string; aviso: { texto: string; ambar: boolean } | null }) {
  if (!aviso) return null;
  return (
    <FieldDescription id={id} className={cn('m-0 mr-auto min-w-0', aviso.ambar && 'text-warning')}>
      {aviso.texto}
    </FieldDescription>
  );
}

// Opciones de las TarjetasRadio de este archivo (R1: reemplazan a
// `Segmentado`, que dibujaba un radiogroup con `Radio.Root` crudo —
// plan_migracion_reui.md §3 R1, DP-R1-a). La elegida sale de `ui/field.tsx`
// (Ajuste 8 de R0: `has-data-checked:border-primary-solid`, F 28/9 — antes
// `border-ring`, dos naranjas distintos entre temas; queda naranja): toda
// tarjeta elegida usa el mismo tratamiento, sin distinguir «Sí» del resto
// (la naranja especial de ¿Salta? no vuelve — la decide ahora el token
// único de «elegido», no esta capa).
const OPCIONES_NO_SI: OpcionTarjeta<'no' | 'si'>[] = [
  { valor: 'no', titulo: 'No' },
  { valor: 'si', titulo: 'Sí' },
];
const OPCIONES_ENTREGA: OpcionTarjeta<'entregado_obra' | 'entregado_cliente'>[] = [
  { valor: 'entregado_obra', titulo: 'Obra' },
  { valor: 'entregado_cliente', titulo: 'Cliente' },
];

/** Formato de fecha es-AR, para el hint de los campos de fecha que arrancan
 *  vacíos en el panel (Ajuste tras la crítica #6): WebKit no pinta ninguna
 *  pista de formato en un `<input type=date>` vacío (a diferencia de Chrome,
 *  que muestra "dd/mm/aaaa" en gris) — este texto cubre ese hueco en Safari.
 *  Solo en el panel: en la obra el campo arranca con la fecha de hoy. */
const FORMATO_FECHA_HINT = 'Formato: dd/mm/aaaa';

/** Reporta hacia arriba el `isPending` de la mutation del form (el panel no
 *  deja cerrar con el POST en vuelo) y vuelve a false al desmontar. Solo
 *  avisa los cambios: el valor inicial (false) es el default de afuera. */
function useReportarPendiente(pending: boolean, onPendingChange?: (pending: boolean) => void): void {
  const onPendingChangeRef = useRef(onPendingChange);
  useEffect(() => {
    onPendingChangeRef.current = onPendingChange;
  });
  useEffect(() => {
    if (!pending) return;
    const avisar = onPendingChangeRef.current;
    avisar?.(true);
    return () => avisar?.(false);
  }, [pending]);
}

/** Props comunes de cada form concreto. */
interface FormBaseProps {
  obra: ObraRef;
  rdt: RdtDetalle;
  onClose: () => void;
  onCancel?: () => void;
  flush?: boolean;
  onPendingChange?: (pending: boolean) => void;
}

/** Chequeo, monto e hito: los tres que monta el panel del Avivador. */
interface FormConVarianteProps extends FormBaseProps {
  variante: VariantePaso;
  onRegistrado?: (r: ResultadoPaso) => void;
  sinTitulo?: boolean;
}

// Formularios contextuales de "+ Paso" (chequeo / monto informado /
// expediente / completar hito / contacto / adjuntar archivo / comentario) —
// se renderizan arriba del hilo de Actividad. Cada uno llama a su mutation y
// cierra el form al terminar; los 409/422 del backend se muestran EN el
// toast con el mensaje legible del backend, no uno genérico.
// «Comentario» (Juan, validación v0.12.0/2026-09-15: «todo es por
// actividad») entra al mismo dispatch — a diferencia del resto, el `tipo`
// de este caso NO llega vía el `pasoAbierto` que dueña obra-detalle.tsx
// (ese estado excluye 'comentario' a propósito, ver PasoAbierto ahí): lo
// abre actividad.tsx con estado local propio, montando este mismo
// PasoForm con tipo="comentario" en el mismo slot.
//
// Props (contrato `PasoFormProps` de components/avivador/panel-avivador-tipos.ts):
// - `onCancel`: cancelar explícito del form (botón "Cancelar" de
//   `PasoFormActs`) — `panel-avivador-accion.tsx` lo pasa para volver a la
//   card sin registrar nada, distinto del camino de éxito (`onClose`/
//   `onRegistrado`). Cuando no se pasa, el fallback es `onClose` —
//   comportamiento actual, sin cambios para la obra.
// - `flush`: ver `PasoFormShell` — se reenvía tal cual al form concreto que
//   corresponda.
// - T4a del Avivador v2 (§13 7.2, Ajuste #9), todo opcional; sin ellas la
//   obra y la home quedan EXACTAMENTE como hoy (fecha de hoy, toast verde,
//   sin «Falta:»): `variante="avivador"` (chequeo, monto e hito: fecha
//   vacía, fecha de índices obligatoria, TarjetasRadio, «Falta: …», botón
//   por paso, sin toast de éxito y sin `onClose` al registrar), `onRegistrado`
//   (con el id / código de la mutation; en 'obra' se llama antes de
//   `onClose`), `onPendingChange` (cualquier tipo) y `sinTitulo`. Los otros
//   cuatro tipos (expediente, contacto, adjunto, comentario) no los monta el
//   panel: ignoran la variante.
export function PasoForm({
  tipo,
  hitoKey,
  obra,
  rdt,
  onClose,
  onCancel,
  flush,
  variante = 'obra',
  onRegistrado,
  onPendingChange,
  sinTitulo,
}: PasoFormProps) {
  const base: FormBaseProps = { obra, rdt, onClose, onCancel, flush, onPendingChange };
  const conVariante: FormConVarianteProps = { ...base, variante, onRegistrado, sinTitulo };
  if (tipo === 'chequeo') return <ChequeoForm {...conVariante} />;
  if (tipo === 'monto') return <FormMonto {...conVariante} />;
  if (tipo === 'expediente') return <FormExpediente {...base} />;
  if (tipo === 'hito') return <HitoForm {...conVariante} hitoKey={hitoKey} />;
  if (tipo === 'contacto') return <ContactoForm {...base} />;
  if (tipo === 'comentario') return <ComentarioForm {...base} />;
  return <AdjuntoForm {...base} />;
}

// Paso "Comentario" (Juan, validación v0.12.0/2026-09-15: «todo es por
// actividad» — deja de ser un composer flotante siempre visible arriba del
// hilo y pasa a abrirse desde «+ Paso» como uno más, en este mismo slot).
// Misma mutación (useCrearComentario) y mismo criterio de atajos que el
// composer viejo: Ctrl/⌘+Enter envía, Escape cancela (el resto de los pasos
// no lo necesita — no tienen un campo de texto libre como target natural
// del atajo).
function ComentarioForm({ obra, rdt, onClose, onCancel, flush, onPendingChange }: FormBaseProps) {
  const crearComentario = useCrearComentario();
  useReportarPendiente(crearComentario.isPending, onPendingChange);
  const [texto, setTexto] = useState('');

  function handleSubmit(): void {
    const trimmed = texto.trim();
    if (!trimmed) return;
    crearComentario.mutate(
      { cc: obra.cc, id: rdt.id, texto: trimmed },
      {
        onSuccess: () => {
          toast.success('Comentario agregado');
          onClose();
        },
        onError: err => toast.error(errorMessage(err, 'No se pudo agregar el comentario')),
      },
    );
  }

  return (
    <PasoFormShell tipo="comentario" titulo="Comentario" flush={flush}>
      <Textarea
        value={texto}
        onChange={e => setTexto(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Escape') {
            // Review adversarial v0.13.0: sin stopPropagation el keydown
            // seguía hasta el listener global de obra-detalle.tsx, que
            // además cierra el drawer de Actividad en <1280px — una sola
            // tecla cerraba el mini-form Y el panel entero, y perdía el
            // borrador. Escape acá equivale a «Cancelar»: solo este form.
            e.stopPropagation();
            (onCancel ?? onClose)();
          } else if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') handleSubmit();
        }}
        placeholder="Escribir comentario…"
        aria-label="Escribir comentario"
        rows={3}
        className="min-h-16 resize-y bg-surface-page text-[13px]"
        autoFocus
      />
      <FieldDescription className="m-0 flex items-center gap-1.5">
        <KbdGroup>
          <Kbd>{isMacPlatform() ? '⌘' : 'Ctrl'}</Kbd>
          <Kbd>Enter</Kbd>
        </KbdGroup>
        para enviar
      </FieldDescription>
      <PasoFormActs onCancel={onCancel ?? onClose} disabled={crearComentario.isPending}>
        <Button type="button" size="sm" onClick={handleSubmit} disabled={crearComentario.isPending || !texto.trim()}>
          {crearComentario.isPending ? 'Enviando…' : 'Comentar'}
        </Button>
      </PasoFormActs>
    </PasoFormShell>
  );
}

/** Label es-AR de una opción del select de período: "may-26" + el sufijo de
 *  "índices aún no publicados" cuando el helper de dominio la marca —
 *  seleccionable igual (el día D RECLAMA, no PROHÍBE — resolución #1). */
function labelOpcionPeriodo(o: { periodo: string; noPublicado: boolean }): string {
  return `${perLabel(o.periodo)}${o.noPublicado ? ' · índices aún no publicados' : ''}`;
}

function ChequeoForm({
  obra,
  rdt,
  onClose,
  onCancel,
  flush,
  onPendingChange,
  variante,
  onRegistrado,
  sinTitulo,
}: FormConVarianteProps) {
  // Panel del Avivador (§13 7.2): la fecha arranca VACÍA y es obligatoria
  // (en la obra viene precargada con hoy y es opcional); ¿Salta? es
  // TarjetasRadio en las dos variantes (R1: sale el Select de la obra);
  // «Falta: …» al lado del botón; sin toast de éxito.
  const enPanel = variante === 'avivador';
  const crearChequeo = useCrearChequeo();
  useReportarPendiente(crearChequeo.isPending, onPendingChange);
  const idSalta = useId();
  const idAviso = useId();
  const idFormatoFecha = useId();
  const opciones = useMemo(() => opcionesPeriodo(rdt), [rdt]);
  const [periodo, setPeriodo] = useState(() => periodoPrecargado(rdt));
  const [pct, setPct] = useState<number | null>(null);
  const [salta, setSalta] = useState(false);
  const [fechaIndices, setFechaIndices] = useState(() => (enPanel ? '' : hoyIso()));
  const [nota, setNota] = useState('');
  const faltan = faltantesPaso('chequeo', { periodo, fechaIndices }, variante);
  const aviso = enPanel ? avisoAcciones(faltan, null, esFechaFutura(fechaIndices)) : null;
  const mostrarFormato = enPanel && !fechaIndices;
  const fechaFuturaObra = !enPanel && esFechaFutura(fechaIndices);

  function handleSubmit(): void {
    // Misma condición que el `disabled` del botón (en el panel queda enfocable).
    if (crearChequeo.isPending || faltan.length > 0) return;
    crearChequeo.mutate(
      {
        cc: obra.cc,
        id: rdt.id,
        periodo,
        valorPct: pct,
        salta,
        fechaIndices: fechaIndices || null,
        nota: nota.trim() || null,
      },
      {
        onSuccess: data => {
          const resultado: ResultadoPaso = { tipo: 'chequeo', id: data.id, periodo, salta };
          if (enPanel) {
            onRegistrado?.(resultado);
            return;
          }
          toast.success(salta ? 'Saltó — arranca la presentación' : 'Chequeo registrado: no salta');
          onRegistrado?.(resultado);
          onClose();
        },
        onError: err => toast.error(errorMessage(err, 'No se pudo registrar el chequeo')),
      },
    );
  }

  return (
    <PasoFormShell tipo="chequeo" titulo="Registrar chequeo de índices" flush={flush} variante={variante} sinTitulo={sinTitulo}>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(130px,1fr))] gap-2">
        <Field>
          <FieldLabel htmlFor="pf-periodo">Período</FieldLabel>
          <Select value={periodo} onValueChange={v => v && setPeriodo(v)}>
            <SelectTrigger id="pf-periodo" className="w-full">
              <SelectValue>{(v: string) => (v ? perLabel(v) : '—')}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {opciones.map(o => (
                <SelectItem key={o.periodo} value={o.periodo}>
                  {labelOpcionPeriodo(o)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field>
          <FieldLabel htmlFor="pf-pct">% calculado (opcional)</FieldLabel>
          <CampoDecimal id="pf-pct" adorno="%" valorInicial={null} onValueChange={setPct} placeholder="—" />
        </Field>
      </div>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(130px,1fr))] gap-2">
        <FieldSet>
          <FieldLegend variant="label" id={idSalta}>
            ¿Salta?
          </FieldLegend>
          <TarjetasRadio
            aria-labelledby={idSalta}
            valor={salta ? 'si' : 'no'}
            onValorChange={v => setSalta(v === 'si')}
            opciones={OPCIONES_NO_SI}
            columnas={2}
          />
        </FieldSet>
        <Field>
          <FieldLabel htmlFor="pf-fi">Fecha de índices</FieldLabel>
          <Input
            id="pf-fi"
            type="date"
            value={fechaIndices}
            onChange={e => setFechaIndices(e.target.value)}
            aria-required={enPanel || undefined}
            aria-describedby={mostrarFormato ? idFormatoFecha : fechaFuturaObra ? idAviso : undefined}
          />
          {mostrarFormato && <FieldDescription id={idFormatoFecha}>{FORMATO_FECHA_HINT}</FieldDescription>}
          {fechaFuturaObra && (
            <FieldDescription id={idAviso} className="text-warning">
              La fecha es futura
            </FieldDescription>
          )}
        </Field>
      </div>
      <Field>
        <FieldLabel htmlFor="pf-nota">Nota (opcional)</FieldLabel>
        <Input id="pf-nota" value={nota} onChange={e => setNota(e.target.value)} placeholder="Ej: supera el umbral del contrato" />
      </Field>
      <PasoFormActs
        onCancel={onCancel ?? onClose}
        disabled={crearChequeo.isPending}
        enPanel={enPanel}
        aviso={<AvisoAcciones id={idAviso} aviso={aviso} />}
      >
        <Button
          type="button"
          size={enPanel ? 'default' : 'sm'}
          onClick={handleSubmit}
          disabled={crearChequeo.isPending || faltan.length > 0}
          focusableWhenDisabled={enPanel}
          aria-describedby={aviso ? idAviso : undefined}
        >
          {crearChequeo.isPending ? 'Registrando…' : enPanel ? 'Registrar chequeo' : 'Registrar'}
        </Button>
      </PasoFormActs>
    </PasoFormShell>
  );
}

// Paso "Monto informado" (SOLO Definitivas — modelo "lo mismo, pero con
// montos", Juan 2026-07-11): el más nuevo (por fecha de negocio) es el que
// va a la lápida (last-wins), el hilo conserva la historia completa.
// Monto y % son los dos OPCIONALES, al menos uno (decisión de Juan, QA
// 2026-09-14): las Definitivas de SOFSE y ADIF se gestionan por FRI/% en vez
// de monto (98 y 83 redes con "FRI 1,0387 (3,87 % en base al último salto)"
// en comentarios, hoy sin otro lugar donde vivir) — el factor FRI no se
// modela, alcanza el %.
function FormMonto({
  obra,
  rdt,
  onClose,
  onCancel,
  flush,
  onPendingChange,
  variante,
  onRegistrado,
  sinTitulo,
}: FormConVarianteProps) {
  // Panel del Avivador (§13 7.2): fecha VACÍA, labels del artifact
  // (relevamiento de la página §6.4), «Falta: …» y sin toast de éxito.
  const enPanel = variante === 'avivador';
  const crearMonto = useCrearMonto();
  useReportarPendiente(crearMonto.isPending, onPendingChange);
  const idAviso = useId();
  const idFormatoFecha = useId();
  // Rango simple para Definitivas (no hay chequeos/día D): últimos 18 meses
  // hasta el corriente, precarga el período habilitado (mismo helper que las
  // Provisorias, sin el matiz "no publicado" que acá no aplica).
  const opciones = useMemo(() => ultimosPeriodos(periodoActual(), 18), []);
  const [monto, setMonto] = useState<number | null>(null);
  const [pct, setPct] = useState<number | null>(null);
  const [periodo, setPeriodo] = useState(() => periodoHabilitado());
  const [fecha, setFecha] = useState(() => (enPanel ? '' : hoyIso()));
  const [nota, setNota] = useState('');
  // Monto y % OPCIONALES, al menos uno; período y fecha obligatorios (el
  // período viene precargado, así que en la obra el botón depende del dato y
  // la fecha, como siempre).
  const faltan = faltantesPaso('monto', { monto, pct, periodo, fecha }, variante);
  const aviso = enPanel ? avisoAcciones(faltan, null, esFechaFutura(fecha)) : null;
  const mostrarFormato = enPanel && !fecha;
  const fechaFuturaObra = !enPanel && esFechaFutura(fecha);

  function handleSubmit(): void {
    // Misma condición que el `disabled` del botón (en el panel queda enfocable).
    if (crearMonto.isPending || faltan.length > 0) return;
    crearMonto.mutate(
      { cc: obra.cc, id: rdt.id, monto, valorPct: pct, periodo, fecha, nota: nota.trim() || null },
      {
        onSuccess: data => {
          const resultado: ResultadoPaso = { tipo: 'monto', id: data.id };
          if (enPanel) {
            onRegistrado?.(resultado);
            return;
          }
          toast.success('Monto informado registrado');
          onRegistrado?.(resultado);
          onClose();
        },
        onError: err => toast.error(errorMessage(err, 'No se pudo registrar el monto')),
      },
    );
  }

  return (
    <PasoFormShell tipo="monto" titulo="Monto informado" flush={flush} variante={variante} sinTitulo={sinTitulo}>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(130px,1fr))] gap-2">
        <Field>
          <FieldLabel htmlFor="pf-monto">{enPanel ? 'Monto (opcional)' : 'Monto (neto, sin IVA)'}</FieldLabel>
          <CampoDecimal id="pf-monto" adorno="$" valorInicial={null} onValueChange={setMonto} placeholder="0,00" />
        </Field>
        <Field>
          <FieldLabel htmlFor="pf-pct-monto">{enPanel ? '% informado (opcional)' : 'Porcentaje'}</FieldLabel>
          <CampoDecimal id="pf-pct-monto" adorno="%" valorInicial={null} onValueChange={setPct} placeholder="—" />
        </Field>
      </div>
      {enPanel ? (
        <p className="-mt-1 text-[11px] text-text-tertiary">Cargá el monto, el %, o los dos (SOFSE y ADIF informan por FRI/%).</p>
      ) : (
        <p className="text-[10.5px] text-text-tertiary">Cargá el monto, el porcentaje o los dos.</p>
      )}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(130px,1fr))] gap-2">
        <Field>
          <FieldLabel htmlFor="pf-periodo-monto">Período</FieldLabel>
          <Select value={periodo} onValueChange={v => v && setPeriodo(v)}>
            <SelectTrigger id="pf-periodo-monto" className="w-full">
              <SelectValue>{(v: string) => (v ? perLabel(v) : '—')}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {opciones.map(p => (
                <SelectItem key={p} value={p}>
                  {perLabel(p)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field>
          <FieldLabel htmlFor="pf-fecha-monto">{enPanel ? 'Fecha' : 'Fecha del cálculo'}</FieldLabel>
          <Input
            id="pf-fecha-monto"
            type="date"
            value={fecha}
            onChange={e => setFecha(e.target.value)}
            aria-required={enPanel || undefined}
            aria-describedby={mostrarFormato ? idFormatoFecha : fechaFuturaObra ? idAviso : undefined}
          />
          {mostrarFormato && <FieldDescription id={idFormatoFecha}>{FORMATO_FECHA_HINT}</FieldDescription>}
          {fechaFuturaObra && (
            <FieldDescription id={idAviso} className="text-warning">
              La fecha es futura
            </FieldDescription>
          )}
        </Field>
      </div>
      <Field>
        <FieldLabel htmlFor="pf-nota-monto">Nota (opcional)</FieldLabel>
        <Input id="pf-nota-monto" value={nota} onChange={e => setNota(e.target.value)} placeholder="Ej: recálculo tras ajuste de mano de obra" />
      </Field>
      <PasoFormActs
        onCancel={onCancel ?? onClose}
        disabled={crearMonto.isPending}
        enPanel={enPanel}
        aviso={<AvisoAcciones id={idAviso} aviso={aviso} />}
      >
        <Button
          type="button"
          size={enPanel ? 'default' : 'sm'}
          onClick={handleSubmit}
          disabled={crearMonto.isPending || faltan.length > 0}
          focusableWhenDisabled={enPanel}
          aria-describedby={aviso ? idAviso : undefined}
        >
          {crearMonto.isPending ? 'Registrando…' : enPanel ? 'Informar monto' : 'Registrar'}
        </Button>
      </PasoFormActs>
    </PasoFormShell>
  );
}

// Paso "Expediente" (ambos tipos): misma regla last-wins que Monto, sin
// fecha de negocio propia (gana por creado_at/id).
function FormExpediente({ obra, rdt, onClose, onCancel, flush, onPendingChange }: FormBaseProps) {
  const crearReferencia = useCrearReferencia();
  useReportarPendiente(crearReferencia.isPending, onPendingChange);
  const [referencia, setReferencia] = useState('');
  const [nota, setNota] = useState('');

  function handleSubmit(): void {
    const trimmed = referencia.trim();
    if (!trimmed) return;
    crearReferencia.mutate(
      { cc: obra.cc, id: rdt.id, referencia: trimmed, nota: nota.trim() || null },
      {
        onSuccess: () => {
          toast.success('Expediente registrado');
          onClose();
        },
        onError: err => toast.error(errorMessage(err, 'No se pudo registrar el expediente')),
      },
    );
  }

  return (
    <PasoFormShell tipo="expediente" titulo="Registrar expediente" flush={flush}>
      <Field>
        <FieldLabel htmlFor="pf-referencia">Referencia *</FieldLabel>
        <Input
          id="pf-referencia"
          value={referencia}
          onChange={e => setReferencia(e.target.value)}
          maxLength={255}
          placeholder="Ej: EX-2024-07973670"
          className="font-mono"
        />
      </Field>
      <Field>
        <FieldLabel htmlFor="pf-nota-exp">Nota (opcional)</FieldLabel>
        <Input id="pf-nota-exp" value={nota} onChange={e => setNota(e.target.value)} placeholder="Ej: caratulado en Mesa de Entradas" />
      </Field>
      <PasoFormActs onCancel={onCancel ?? onClose} disabled={crearReferencia.isPending}>
        <Button type="button" size="sm" onClick={handleSubmit} disabled={crearReferencia.isPending || !referencia.trim()}>
          {crearReferencia.isPending ? 'Registrando…' : 'Registrar'}
        </Button>
      </PasoFormActs>
    </PasoFormShell>
  );
}

// Panel del Avivador (T4a, relevamiento de la página §6.4): el botón y el
// placeholder de la nota según el hito que se registra (el próximo paso
// REAL de la rede, no la rama: una R3c aprobada con póliza pendiente
// registra la póliza, DP-20).
const ETIQUETA_HITO_PANEL: Record<string, string> = {
  entregado_obra: 'Registrar entrega',
  entregado_cliente: 'Registrar entrega',
  aprobado: 'Registrar aprobación',
  poliza_ec_enviada: 'Registrar póliza',
  facturado: 'Marcar facturado',
};
const PLACEHOLDER_NOTA_HITO_PANEL: Record<string, string> = {
  entregado_obra: 'Ej: presentada por mesa de entradas',
  entregado_cliente: 'Ej: presentada por mesa de entradas',
  aprobado: 'Ej: firmado en acta de recepción',
  poliza_ec_enviada: 'Ej: póliza enviada al cliente',
  facturado: 'Ej: factura A 0003-00001234',
};

function HitoForm({
  obra,
  rdt,
  hitoKey,
  onClose,
  onCancel,
  flush,
  onPendingChange,
  variante,
  onRegistrado,
  sinTitulo,
}: FormConVarianteProps & { hitoKey?: string }) {
  // Panel del Avivador (§13 7.2, Ajuste #9): fecha VACÍA, «Entregada a» y
  // «Requiere póliza EC» como TarjetasRadio, el hito fijo en una caja de solo
  // lectura, «Falta: …» (o el motivo de la entrega bloqueada) al lado del
  // botón, botón por hito y sin toast de éxito.
  const enPanel = variante === 'avivador';
  const completarHito = useCompletarHito();
  useReportarPendiente(completarHito.isPending, onPendingChange);
  const idEntrega = useId();
  const idPoliza = useId();
  const idAviso = useId();
  const idFormatoFecha = useId();
  const idFechaFuturaObra = useId();
  const efectivoKey = hitoKey ?? proximoPaso(lifecycle(rdt));
  const esEntrega = efectivoKey === 'entrega';
  const codigoFijo = efectivoKey ? STEP_KEY_TO_HITO_CODIGO[efectivoKey] : undefined;
  const nombreFijo = codigoFijo ? (hitoAplicable(rdt.hitos, codigoFijo)?.nombre ?? codigoFijo) : undefined;
  // Si la rama de entrega ya fue ELEGIDA (fila sin fecha todavía, ej. tras
  // Editar) hay que precargarla — el default fijo en "Entregado a obra"
  // ignoraba la elección real y, si era "al cliente", el envío sin tocar el
  // select pegaba un 422 desconcertante del backend (XOR ya resuelto).
  // Reportado por 2 revisores, 2026-07-10.
  const entregaYaElegida = esEntrega ? entregaElegida(rdt.hitos) : null;
  // R1 (Juan, 2026-09-14): la entrega SIEMPRE pasa por esta guarda al
  // completarla (POST /hitos/completar la aplica sin excepción para hitos de
  // entrega) — también cuando `entregaYaElegida` ya existe (una rama elegida
  // sin fecha todavía, ej. tras Editar): esa fila igual necesita el salto/el
  // monto antes de que se le pueda cargar la fecha.
  const motivoBloqueada = esEntrega ? motivoEntregaBloqueada(rdt) : null;

  const [codigo, setCodigo] = useState<string>(
    esEntrega ? (entregaYaElegida?.codigo ?? 'entregado_obra') : (codigoFijo ?? ''),
  );
  const [fecha, setFecha] = useState(() => (enPanel ? '' : hoyIso()));
  // "Requiere póliza EC" del paso Aprobado (resolución #8 del panel
  // adversarial): reemplaza el toggle que antes vivía en el form de
  // alta/edición — solo relevante cuando el hito que se está completando es
  // 'aprobado'. R1: TarjetasRadio en las dos variantes (sale el Switch de la
  // obra).
  const [polizaAplica, setPolizaAplica] = useState(false);
  const [nota, setNota] = useState('');

  if (!efectivoKey || (!esEntrega && !codigoFijo)) {
    // R1 (Juan, 2026-09-14): `efectivoKey` puede resolver a 'salto'/'calculado'
    // (el primer paso del ciclo, que abre Chequeo de índices/Monto informado,
    // no un hito) — actividad.tsx ya intercepta ese caso en handlePaletteSelect
    // y en circunstancias normales no debería llegar hasta acá, pero si algún
    // día lo hace (ej. `rdt` se refresca entre el click de "+ Paso" y este
    // render) hay que decir la verdad: falta el primer paso, no "sobra" nada
    // — antes esta rama mostraba "El ciclo ya está completo" también en ese
    // caso, un mensaje directamente falso.
    const faltaPrimerPaso = efectivoKey === 'salto' || efectivoKey === 'calculado';
    return (
      <PasoFormShell tipo="hito" titulo="Completar hito" flush={flush} variante={variante} sinTitulo={sinTitulo}>
        <p className="text-xs text-muted-foreground">
          {faltaPrimerPaso
            ? `Registrá primero ${efectivoKey === 'salto' ? 'el chequeo de índices' : 'el monto calculado'} — desde «+ Paso».`
            : 'El ciclo de esta redeterminación ya está completo.'}
        </p>
        <PasoFormActs onCancel={onCancel ?? onClose} enPanel={enPanel} />
      </PasoFormShell>
    );
  }

  const faltan = faltantesPaso('hito', { fecha }, variante);
  const aviso = enPanel ? avisoAcciones(faltan, motivoBloqueada, esFechaFutura(fecha)) : null;
  const mostrarFormato = enPanel && !fecha;
  const fechaFuturaObra = !enPanel && esFechaFutura(fecha);

  function handleSubmit(): void {
    // Misma condición que el `disabled` del botón (en el panel queda enfocable).
    if (completarHito.isPending || faltan.length > 0 || motivoBloqueada !== null || !codigo) return;
    const codigoEnviado = codigo;
    // E4 del review de T4a (ResultadoPaso.polizaAgregada): el backend agrega la
    // póliza EC en este POST solo si la rede no tenía la fila (con o sin fecha).
    // Se calcula con la ficha de ANTES del POST.
    const polizaAgregada = codigo === 'aprobado' && polizaAplica && hitoAplicable(rdt.hitos, 'poliza_ec_enviada') === null;
    completarHito.mutate(
      { cc: obra.cc, id: rdt.id, codigo, fecha, nota: nota.trim() || null, ...(codigo === 'aprobado' ? { polizaAplica } : {}) },
      {
        onSuccess: () => {
          const resultado: ResultadoPaso = { tipo: 'hito', codigo: codigoEnviado, ...(polizaAgregada ? { polizaAgregada } : {}) };
          if (enPanel) {
            onRegistrado?.(resultado);
            return;
          }
          toast.success('Hito completado');
          onRegistrado?.(resultado);
          onClose();
        },
        onError: err => toast.error(errorMessage(err, 'No se pudo completar el hito')),
      },
    );
  }

  if (enPanel) {
    const campoFecha = (
      <Field>
        <FieldLabel htmlFor="pf-fecha-hito">Fecha</FieldLabel>
        <Input
          id="pf-fecha-hito"
          type="date"
          value={fecha}
          onChange={e => setFecha(e.target.value)}
          aria-required
          aria-describedby={mostrarFormato ? idFormatoFecha : undefined}
        />
        {mostrarFormato && <FieldDescription id={idFormatoFecha}>{FORMATO_FECHA_HINT}</FieldDescription>}
      </Field>
    );
    return (
      <PasoFormShell tipo="hito" titulo="Completar hito" flush={flush} variante={variante} sinTitulo={sinTitulo}>
        {esEntrega ? (
          <>
            <FieldSet>
              <FieldLegend variant="label" id={idEntrega}>
                Entregada a
              </FieldLegend>
              {/* Bloqueado si la rama ya fue elegida antes (edición sin
                  fecha): el backend rechaza la otra (XOR), como en la obra. */}
              <TarjetasRadio
                aria-labelledby={idEntrega}
                valor={codigo === 'entregado_cliente' ? 'entregado_cliente' : 'entregado_obra'}
                onValorChange={setCodigo}
                opciones={OPCIONES_ENTREGA}
                columnas={2}
                disabled={Boolean(entregaYaElegida)}
              />
            </FieldSet>
            {campoFecha}
          </>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fit,minmax(130px,1fr))] gap-2">
            <Field>
              <FieldLabel htmlFor="pf-hito-fijo">Hito</FieldLabel>
              <Input id="pf-hito-fijo" value={nombreFijo ?? ''} readOnly tabIndex={-1} />
            </Field>
            {campoFecha}
          </div>
        )}
        {codigo === 'aprobado' && (
          <FieldSet>
            <FieldLegend variant="label" id={idPoliza}>
              Requiere póliza EC
            </FieldLegend>
            <TarjetasRadio
              aria-labelledby={idPoliza}
              valor={polizaAplica ? 'si' : 'no'}
              onValorChange={v => setPolizaAplica(v === 'si')}
              opciones={OPCIONES_NO_SI}
              columnas={2}
            />
          </FieldSet>
        )}
        <Field>
          <FieldLabel htmlFor="pf-nota-hito">Nota (opcional)</FieldLabel>
          <Input
            id="pf-nota-hito"
            value={nota}
            onChange={e => setNota(e.target.value)}
            placeholder={PLACEHOLDER_NOTA_HITO_PANEL[codigo] ?? 'Ej: firmado en acta de recepción'}
          />
        </Field>
        <PasoFormActs
          onCancel={onCancel ?? onClose}
          disabled={completarHito.isPending}
          enPanel
          aviso={<AvisoAcciones id={idAviso} aviso={aviso} />}
        >
          <Button
            type="button"
            size="default"
            onClick={handleSubmit}
            disabled={completarHito.isPending || faltan.length > 0 || motivoBloqueada !== null}
            focusableWhenDisabled
            aria-describedby={aviso ? idAviso : undefined}
          >
            {completarHito.isPending ? 'Registrando…' : (ETIQUETA_HITO_PANEL[codigo] ?? 'Registrar')}
          </Button>
        </PasoFormActs>
      </PasoFormShell>
    );
  }

  return (
    <PasoFormShell tipo="hito" titulo="Completar hito" flush={flush}>
      {esEntrega && (
        // Misma forma que el panel (decisión 2 de Juan: tarjetas de radio para
        // elegir UNA opción). Bloqueado si la rama ya fue elegida antes
        // (edición sin fecha): el backend rechaza la otra (XOR).
        <FieldSet>
          <FieldLegend variant="label" id={idEntrega}>
            Entregada a
          </FieldLegend>
          <TarjetasRadio
            aria-labelledby={idEntrega}
            valor={codigo === 'entregado_cliente' ? 'entregado_cliente' : 'entregado_obra'}
            onValorChange={setCodigo}
            opciones={OPCIONES_ENTREGA}
            columnas={2}
            disabled={Boolean(entregaYaElegida)}
          />
        </FieldSet>
      )}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(130px,1fr))] gap-2">
        {!esEntrega && (
          <Field>
            <FieldLabel htmlFor="pf-hito-fijo-obra">Hito</FieldLabel>
            <Input id="pf-hito-fijo-obra" value={nombreFijo ?? ''} readOnly tabIndex={-1} />
          </Field>
        )}
        <Field>
          <FieldLabel htmlFor="pf-fecha-hito">Fecha</FieldLabel>
          <Input
            id="pf-fecha-hito"
            type="date"
            value={fecha}
            onChange={e => setFecha(e.target.value)}
            aria-describedby={fechaFuturaObra ? idFechaFuturaObra : undefined}
          />
          {fechaFuturaObra && (
            <FieldDescription id={idFechaFuturaObra} className="text-warning">
              La fecha es futura
            </FieldDescription>
          )}
        </Field>
      </div>
      {/* R1 (Juan, 2026-09-14): mismo texto que el 422 del backend — se avisa
          inline y se deshabilita "Completar" en vez de dejar que el submit
          rebote con un error recién al guardar. */}
      {motivoBloqueada && (
        <FieldDescription id={idAviso} className="text-warning">
          {motivoBloqueada}
        </FieldDescription>
      )}
      {codigo === 'aprobado' && (
        <FieldSet>
          <FieldLegend variant="label" id={idPoliza}>
            Requiere póliza EC
          </FieldLegend>
          <TarjetasRadio
            aria-labelledby={idPoliza}
            valor={polizaAplica ? 'si' : 'no'}
            onValorChange={v => setPolizaAplica(v === 'si')}
            opciones={OPCIONES_NO_SI}
            columnas={2}
          />
        </FieldSet>
      )}
      <Field>
        <FieldLabel htmlFor="pf-nota-hito-obra">Nota (opcional)</FieldLabel>
        <Input id="pf-nota-hito-obra" value={nota} onChange={e => setNota(e.target.value)} placeholder="Ej: firmado en acta de recepción" />
      </Field>
      <PasoFormActs onCancel={onCancel ?? onClose} disabled={completarHito.isPending}>
        <Button
          type="button"
          size="sm"
          onClick={handleSubmit}
          disabled={completarHito.isPending || !fecha || motivoBloqueada !== null}
          aria-describedby={motivoBloqueada ? idAviso : undefined}
        >
          {completarHito.isPending ? 'Completando…' : 'Completar'}
        </Button>
      </PasoFormActs>
    </PasoFormShell>
  );
}

function ContactoForm({ obra, rdt, onClose, onCancel, flush, onPendingChange }: FormBaseProps) {
  const crearSeguimiento = useCrearSeguimiento();
  useReportarPendiente(crearSeguimiento.isPending, onPendingChange);
  const [interlocutor, setInterlocutor] = useState('obra');
  const [resultado, setResultado] = useState('sin_respuesta');
  const [fecha, setFecha] = useState(hoyIso());
  const [nota, setNota] = useState('');
  const idFechaFutura = useId();

  const INTERLOCUTOR_LABEL: Record<string, string> = { obra: 'Obra', cliente: 'Cliente', organismo: 'Organismo', otro: 'Otro' };

  function handleSubmit(): void {
    if (!fecha) return;
    crearSeguimiento.mutate(
      { cc: obra.cc, id: rdt.id, interlocutor, resultado, fecha, nota: nota.trim() || null },
      {
        onSuccess: () => {
          toast.success('Contacto registrado');
          onClose();
        },
        onError: err => toast.error(errorMessage(err, 'No se pudo registrar el contacto')),
      },
    );
  }

  return (
    <PasoFormShell tipo="contacto" titulo="Registrar contacto" flush={flush}>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(130px,1fr))] gap-2">
        <Field>
          <FieldLabel htmlFor="pf-inter">Con quién</FieldLabel>
          <Select value={interlocutor} onValueChange={v => v && setInterlocutor(v)}>
            <SelectTrigger id="pf-inter" className="w-full">
              <SelectValue>{(v: string) => INTERLOCUTOR_LABEL[v] ?? v}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="obra">Obra</SelectItem>
              <SelectItem value="cliente">Cliente</SelectItem>
              <SelectItem value="organismo">Organismo</SelectItem>
              <SelectItem value="otro">Otro</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field>
          <FieldLabel htmlFor="pf-res">Resultado</FieldLabel>
          <Select value={resultado} onValueChange={v => v && setResultado(v)}>
            <SelectTrigger id="pf-res" className="w-full">
              <SelectValue>{(v: string) => (v === 'sin_respuesta' ? 'Sin respuesta' : 'Con novedad')}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="sin_respuesta">Sin respuesta</SelectItem>
              <SelectItem value="novedad">Con novedad</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </div>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(130px,1fr))] gap-2">
        <Field>
          <FieldLabel htmlFor="pf-fecha-cont">Fecha</FieldLabel>
          <Input
            id="pf-fecha-cont"
            type="date"
            value={fecha}
            onChange={e => setFecha(e.target.value)}
            aria-describedby={esFechaFutura(fecha) ? idFechaFutura : undefined}
          />
          {esFechaFutura(fecha) && (
            <FieldDescription id={idFechaFutura} className="text-warning">
              La fecha es futura
            </FieldDescription>
          )}
        </Field>
        <Field>
          <FieldLabel htmlFor="pf-nota-cont">Nota (opcional)</FieldLabel>
          <Input id="pf-nota-cont" value={nota} onChange={e => setNota(e.target.value)} placeholder="Ej: quedaron en responder el lunes" />
        </Field>
      </div>
      <PasoFormActs onCancel={onCancel ?? onClose} disabled={crearSeguimiento.isPending}>
        <Button type="button" size="sm" onClick={handleSubmit} disabled={crearSeguimiento.isPending || !fecha}>
          {crearSeguimiento.isPending ? 'Registrando…' : 'Registrar'}
        </Button>
      </PasoFormActs>
    </PasoFormShell>
  );
}

const TIPOS_ADJUNTO: { value: TipoAdjunto; label: string }[] = [
  { value: 'acta', label: 'Acta' },
  { value: 'resolucion', label: 'Resolución' },
  { value: 'otro', label: 'Otro' },
];

function AdjuntoForm({ obra, rdt, onClose, onCancel, flush, onPendingChange }: FormBaseProps) {
  const subirAdjunto = useSubirAdjunto();
  useReportarPendiente(subirAdjunto.isPending, onPendingChange);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const idArchivo = useId();
  const [tipoDoc, setTipoDoc] = useState<TipoAdjunto>('acta');
  const [archivo, setArchivo] = useState<File | null>(null);
  const [nota, setNota] = useState('');

  function handleSubmit(): void {
    if (!archivo) return;
    subirAdjunto.mutate(
      { cc: obra.cc, id: rdt.id, tipo: tipoDoc, archivo, nota: nota.trim() || null },
      {
        onSuccess: () => {
          toast.success('Archivo adjuntado');
          onClose();
        },
        onError: err => toast.error(errorMessage(err, 'No se pudo adjuntar el archivo')),
      },
    );
  }

  return (
    <PasoFormShell tipo="adjunto" titulo="Adjuntar archivo" flush={flush}>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(130px,1fr))] gap-2">
        <Field>
          <FieldLabel htmlFor="pf-tipodoc">Tipo de documento</FieldLabel>
          <Select value={tipoDoc} onValueChange={v => v && setTipoDoc(v as TipoAdjunto)}>
            <SelectTrigger id="pf-tipodoc" className="w-full">
              <SelectValue>{(v: string) => TIPOS_ADJUNTO.find(t => t.value === v)?.label ?? v}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {TIPOS_ADJUNTO.map(t => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field>
          {/* R1: el disparador no es un control nativo asociable por
              `htmlFor` — su nombre es la etiqueta más su propio texto
              («Archivo, Elegir archivo…» o el nombre del archivo elegido). */}
          <FieldLabel id={idArchivo}>Archivo</FieldLabel>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            aria-labelledby={`${idArchivo} ${idArchivo}-texto`}
            className="w-full justify-start gap-1.5"
          >
            <Paperclip className="h-3.5 w-3.5" aria-hidden="true" />
            <span id={`${idArchivo}-texto`}>{archivo ? archivo.name : 'Elegir archivo…'}</span>
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.docx,.xlsx"
            className="hidden"
            onChange={e => setArchivo(e.target.files?.[0] ?? null)}
          />
        </Field>
      </div>
      <Field>
        <FieldLabel htmlFor="pf-nota-adj">Nota (opcional)</FieldLabel>
        <Input id="pf-nota-adj" value={nota} onChange={e => setNota(e.target.value)} placeholder="Ej: acta de recepción firmada por DPE" />
      </Field>
      <PasoFormActs onCancel={onCancel ?? onClose} disabled={subirAdjunto.isPending}>
        <Button type="button" size="sm" onClick={handleSubmit} disabled={subirAdjunto.isPending || !archivo}>
          {subirAdjunto.isPending ? 'Adjuntando…' : 'Adjuntar'}
        </Button>
      </PasoFormActs>
    </PasoFormShell>
  );
}
